<template>
    <div class="editor">
        <editor-menu-bubble :editor="editor" :keep-in-bounds="keepInBounds" v-slot="{ commands, isActive, menu }">
            <div
                    class="menububble"
                    :class="{ 'is-active': menu.isActive }"
                    :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
            >
                <bs-action :icon="['fas', 'bold']" class="menububble__button btn-info mr-1" :class="{ 'is-active': isActive.bold() }" @click="commands.bold"></bs-action>
                <bs-action :icon="['fas', 'italic']" class="menububble__button btn-info mr-1" :class="{ 'is-active': isActive.italic() }" @click="commands.italic"></bs-action>

            </div>
        </editor-menu-bubble>

        <div class="suggestion-list" v-show="showSuggestions" ref="suggestions">
            <template v-if="hasResults">
                <div
                        v-for="(user, index) in filteredUsers"
                        :key="user.id"
                        class="suggestion-list__item"
                        :class="{ 'is-selected': navigatedUserIndex === index }"
                        @click="selectUser(user)"
                >
                    {{ user.name }}
                </div>
            </template>
            <div v-else class="suggestion-list__item is-empty">
                No users found
            </div>
        </div>

        <editor-content class="editor__content p-2" :editor="editor" />
        <bs-action :icon="['fas', 'paper-plane']" class="post-comment btn-secure" @click="submit"></bs-action>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import Fuse from 'fuse.js'
import tippy, { sticky } from 'tippy.js'
import { Editor, EditorContent, EditorMenuBubble } from 'tiptap'
import {
  BulletList, HardBreak, Heading, ListItem, OrderedList, Bold, Code, Italic, Link, Strike, Underline,
  Placeholder, Mention
} from 'tiptap-extensions'

export default {
  name: 'Comment',
  props: ['initContent'],
  components: {
    EditorContent,
    EditorMenuBubble
  },
  data () {
    return {
      keepInBounds: true,
      editor: new Editor({
        extensions: [new BulletList(), new HardBreak(), new Heading({ levels: [1, 2, 3] }), new ListItem(),
          new OrderedList(), new Link(), new Bold(), new Code(), new Italic(), new Strike(), new Underline(),
          new Placeholder({
            emptyEditorClass: 'is-editor-empty',
            emptyNodeClass: 'is-empty',
            emptyNodeText: this.$t('messages.type_comment'),
            showOnlyWhenEditable: true,
            showOnlyCurrent: true
          }),
          new Mention({
            // a list of all suggested items
            items: async () => {
              await new Promise(resolve => {
                setTimeout(resolve, 500)
              })
              return this.users
            },
            // is called when a suggestion starts
            onEnter: ({
              items, query, range, command, virtualNode
            }) => {
              this.query = query
              this.filteredUsers = items
              this.suggestionRange = range
              this.renderPopup(virtualNode)
              // we save the command for inserting a selected mention
              // this allows us to call it inside of our custom popup
              // via keyboard navigation and on click
              this.insertMention = command
            },
            // is called when a suggestion has changed
            onChange: ({
              items, query, range, virtualNode
            }) => {
              this.query = query
              this.filteredUsers = items
              this.suggestionRange = range
              this.navigatedUserIndex = 0
              this.renderPopup(virtualNode)
            },
            // is called when a suggestion is cancelled
            onExit: () => {
              // reset all saved values
              this.query = null
              this.filteredUsers = []
              this.suggestionRange = null
              this.navigatedUserIndex = 0
              this.destroyPopup()
            },
            // is called on every keyDown event while a suggestion is active
            onKeyDown: ({ event }) => {
              if (event.key === 'ArrowUp') {
                this.upHandler()
                return true
              }
              if (event.key === 'ArrowDown') {
                this.downHandler()
                return true
              }
              if (event.key === 'Enter') {
                this.enterHandler()
                return true
              }
              return false
            },
            // is called when a suggestion has changed
            // this function is optional because there is basic filtering built-in
            // you can overwrite it if you prefer your own filtering
            // in this example we use fuse.js with support for fuzzy search
            onFilter: async (items, query) => {
              if (!query) {
                return items
              }
              await new Promise(resolve => {
                setTimeout(resolve, 100)
              })
              const fuse = new Fuse(items, {
                threshold: 0.2,
                keys: ['name']
              })
              return fuse.search(query).map(item => item.item)
            }
          })],
        onUpdate: ({ getHTML }) => {
          this.$emit('comment', getHTML())
        }
      }),
      query: null,
      suggestionRange: null,
      filteredUsers: [],
      navigatedUserIndex: 0,
      insertMention: () => {}
    }
  },
  computed: {
    ...mapState({
      users: state => state.usersStore
    }),
    hasResults () {
      return this.filteredUsers.length
    },
    showSuggestions () {
      return this.query || this.hasResults
    }
  },
  watch: {
    initContent (newVal) {
      this.editor.setContent(newVal)
    }
  },
  beforeDestroy () {
    this.editor.destroy()
    this.destroyPopup()
  },
  methods: {
    submit () {
      this.$emit('submit', this.editor.getHTML())
      this.editor.clearContent()
    },
    // navigate to the previous item
    // if it's the first item, navigate to the last one
    upHandler () {
      this.navigatedUserIndex = ((this.navigatedUserIndex + this.filteredUsers.length) - 1) % this.filteredUsers.length
    },
    // navigate to the next item
    // if it's the last item, navigate to the first one
    downHandler () {
      this.navigatedUserIndex = (this.navigatedUserIndex + 1) % this.filteredUsers.length
    },
    enterHandler () {
      const user = this.filteredUsers[this.navigatedUserIndex]
      if (user) {
        this.selectUser(user)
      }
    },
    // we have to replace our suggestion text with a mention
    // so it's important to pass also the position of your suggestion text
    selectUser (user) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user.id,
          label: user.name
        }
      })
      this.editor.focus()
    },
    // renders a popup with suggestions
    // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
    renderPopup (node) {
      if (this.popup) {
        return
      }
      // ref: https://atomiks.github.io/tippyjs/v6/all-props/
      this.popup = tippy('#comment', {
        getReferenceClientRect: node.getBoundingClientRect,
        appendTo: () => document.body,
        interactive: true,
        sticky: true, // make sure position of tippy is updated when content changes
        plugins: [sticky],
        content: this.$refs.suggestions,
        trigger: 'mouseenter', // manual
        showOnCreate: true,
        theme: 'dark',
        placement: 'top-start',
        inertia: true,
        duration: [400, 200]
      })
    },
    destroyPopup () {
      if (this.popup) {
        if (this.popup[0]) this.popup[0].destroy()
        this.popup = null
      }
    }
  }
}
</script>

<style lang="scss">
    .mention {
        background: rgba(black, 0.1);
        color: rgba(black, 0.6);
        font-size: 0.8rem;
        font-weight: bold;
        border-radius: 5px;
        padding: 0.2rem 0.5rem;
        white-space: nowrap;
    }
    .mention-suggestion {
        color: rgba(black, 0.6);
    }
    .suggestion-list {
        padding: 0.2rem;
        border: 2px solid rgba(black, 0.1);
        font-size: 0.8rem;
        font-weight: bold;
        &__no-results {
            padding: 0.2rem 0.5rem;
        }
        &__item {
            border-radius: 5px;
            padding: 0.2rem 0.5rem;
            margin-bottom: 0.2rem;
            cursor: pointer;
            &:last-child {
                margin-bottom: 0;
            }
            &.is-selected,
            &:hover {
                background-color: rgba(white, 0.2);
            }
            &.is-empty {
                opacity: 0.5;
            }
        }
    }
    .tippy-box[data-theme~=dark] {
        background-color: black;
        padding: 0;
        font-size: 1rem;
        text-align: inherit;
        color: white;
        border-radius: 5px;
    }
    .editor {
        position: relative;

        .post-comment {
            position: absolute;
            right: 10px;
            bottom: 10px;
        }
    }

    .editor p.is-editor-empty:first-child::before {
        content: attr(data-empty-text);
        float: left;
        color: #aaa;
        pointer-events: none;
        height: 0;
    }

    .editor__content {
        font-weight: 400;
    }

    .ProseMirror:focus {
        outline: none;
    }

    .menububble {
        position: absolute;
        visibility: hidden;
        display: flex;
        z-index: 20;
        transform: translateX(-50%);
        opacity: 0;
        transition: opacity 0.2s, visibility 0.2s;

        &.is-active {
            visibility: visible;
            opacity: 1;
        }
    }
</style>
